import { getCurrentDomain } from './domain'

export interface BrandingInfo {
    siteName: string
    siteNameShort: string
    tagline: {
        NL: string
        BE: string
        US: string
    }
    description: string
    title: string
    ogTitle: string
    ogDescription: string
    twitterTitle: string
    twitterDescription: string
    canonicalUrl: string
    ogUrl: string
    ogSiteName: string
    appleMobileWebAppTitle: string
    author: string
    favicon: {
        ico: string
        png32: string
        png512: string
        appleTouchIcon: string
    }
    logo: {
        white: string
        yellow: string
    }
}

const SPELLEN_LAND_BRANDING: BrandingInfo = {
    siteName: 'Spellen.land',
    siteNameShort: 'Spellen.land',
    tagline: {
        NL: 'De prijsvergelijker voor spellen in Nederland',
        BE: 'De prijsvergelijker voor spellen in België',
        US: 'The price comparison for games in the United States'
    },
    description: 'Vergelijk spel prijzen tussen platforms en winkels - Vind de beste deals op PlayStation, Xbox, Nintendo Switch en PC games',
    title: 'Spellen.land - De game prijsverlijker van Nederland',
    ogTitle: 'Spellen.land - Spellen Prijsvergelijking',
    ogDescription: 'Vergelijk videospelprijzen tussen platforms en winkels - Vind de beste deals op PlayStation, Xbox, Nintendo Switch en PC games',
    twitterTitle: 'Spellen.land - Spellen Prijsvergelijking',
    twitterDescription: 'Vergelijk videospelprijzen tussen platforms en winkels - Vind de beste deals op PlayStation, Xbox, Nintendo Switch en PC games',
    canonicalUrl: 'https://spellen.land',
    ogUrl: 'https://spellen.land',
    ogSiteName: 'Spellen.land',
    appleMobileWebAppTitle: 'Spellen.land',
    author: 'Spellen.land',
    favicon: {
        ico: '/favicon.ico',
        png32: '/spellen-land-40-white.png',
        png512: '/spellen-land-512-transparent.png',
        appleTouchIcon: '/spellen-land-512-transparent.png'
    },
    logo: {
        white: '/spellen-land-40-white.png',
        yellow: '/spellen-land-40-yellow.png'
    }
}

const VIDEOGAME_LAND_BRANDING: BrandingInfo = {
    siteName: 'Videogame.land',
    siteNameShort: 'Videogame.land',
    tagline: {
        NL: 'The price comparison for videogames',
        BE: 'The price comparison for videogames',
        US: 'The price comparison for videogames'
    },
    description: 'Compare video game prices between platforms and stores - Find the best deals on PlayStation, Xbox, Nintendo Switch and PC games',
    title: 'Videogame.land - The game price comparison for the United States',
    ogTitle: 'Videogame.land - Video Game Price Comparison',
    ogDescription: 'Compare video game prices between platforms and stores - Find the best deals on PlayStation, Xbox, Nintendo Switch and PC games',
    twitterTitle: 'Videogame.land - Video Game Price Comparison',
    twitterDescription: 'Compare video game prices between platforms and stores - Find the best deals on PlayStation, Xbox, Nintendo Switch and PC games',
    canonicalUrl: 'https://videogame.land',
    ogUrl: 'https://videogame.land',
    ogSiteName: 'Videogame.land',
    appleMobileWebAppTitle: 'Videogame.land',
    author: 'Videogame.land',
    favicon: {
        ico: '/favicon.ico',
        png32: '/spellen-land-40-white.png', // Using same assets for now
        png512: '/spellen-land-512-transparent.png',
        appleTouchIcon: '/spellen-land-512-transparent.png'
    },
    logo: {
        white: '/spellen-land-40-white.png', // Using same assets for now
        yellow: '/spellen-land-40-yellow.png'
    }
}

/**
 * Get the branding information based on the current domain
 */
export const getBrandingInfo = (): BrandingInfo => {
    const domain = getCurrentDomain()

    if (domain === 'videogame.land') {
        return VIDEOGAME_LAND_BRANDING
    }

    // Default to spellen.land branding for all other domains
    return SPELLEN_LAND_BRANDING
}

/**
 * Get the current site name
 */
export const getSiteName = (): string => {
    return getBrandingInfo().siteName
}

/**
 * Get the current site name for short display
 */
export const getSiteNameShort = (): string => {
    return getBrandingInfo().siteNameShort
}

/**
 * Get the tagline for a specific country
 */
export const getTagline = (country: string): string => {
    const branding = getBrandingInfo()
    return branding.tagline[country as keyof typeof branding.tagline] || branding.tagline.US
}

/**
 * Get the canonical URL for the current domain
 */
export const getCanonicalUrl = (): string => {
    return getBrandingInfo().canonicalUrl
}

/**
 * Get the full title with site name
 */
export const getFullTitle = (title: string): string => {
    return `${title} | ${getSiteName()}`
} 